import { createFileRoute } from "@tanstack/react-router";
import { PageHeader } from "@/components/page-header";
import { DateRangeFilter } from "@/components/date-range-filter";
import { SetupPanel } from "@/components/setup-panel";
import { SampleBanner } from "@/components/sample-banner";
import { SkHeader, SkMetricChart, SkPanel, SkTiles } from "@/components/skeleton";
import { PageQuery } from "@/components/page-query";
import { AudienceTab, ChannelsTab, OverviewTab } from "@/features/website/tabs";
import { DEFAULT_PRESET } from "@shared/analytics-range";
import type { WebsitePage } from "@shared/pages/website";
import { pageQuery } from "@/lib/queries";
import { useTitle } from "@/lib/title";

export const Route = createFileRoute("/_app/website")({
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchQuery(pageQuery<WebsitePage>("website", deps));
  },
  component: WebsiteRoute,
});

const SECTIONS: Record<string, { title: string; info?: string }> = {
  overview: { title: "Google Analytics" },
  channels: { title: "Channels", info: "Where the visits and the sales came from." },
  audience: { title: "Audience", info: "Who the visitors are: devices, new and returning, places and times." },
};

function Skeleton() {
  return (
    <div className="page-shell">
      <SkHeader />
      <div className="mt-5 space-y-4">
        <SkMetricChart />
        <SkTiles count={4} />
        <div className="grid gap-4 @3xl:grid-cols-2">
          <SkPanel />
        </div>
      </div>
    </div>
  );
}

function WebsiteRoute() {
  useTitle("Google Analytics");
  const search = Route.useSearch();
  return (
    <PageQuery<WebsitePage> name="website" search={search} skeleton={<Skeleton />}>
      {(page) => <Website page={page} />}
    </PageQuery>
  );
}

function Website({ page }: { page: WebsitePage }) {
  const { range: r, report, prev, tab } = page;

  const header = (
    <PageHeader
      title={SECTIONS[tab].title}
      info={SECTIONS[tab].info}
      actions={<DateRangeFilter presets={r.presets} defaultKey={DEFAULT_PRESET} compare />}
    />
  );

  if (!report) {
    return (
      <div className="page-shell">
        {header}
        {page.source === "unavailable" ? (
          <SetupPanel title="Website numbers aren't available right now">
            Google Analytics numbers didn&apos;t come through for this range. Try again in a few minutes; if it keeps
            happening, ask Digital King to check the connection.
          </SetupPanel>
        ) : (
          <SetupPanel title="Google Analytics isn't connected yet">
            Visits, shoppers and orders on the website show here once Digital King connects Google Analytics.
          </SetupPanel>
        )}
      </div>
    );
  }

  return (
    <div className="page-shell">
      {header}
      {page.source === "sample" && <SampleBanner what="Website numbers" source="Google Analytics" />}
      {tab === "overview" && <OverviewTab report={report} prev={prev} r={r} />}
      {tab === "channels" && <ChannelsTab report={report} prev={prev} r={r} />}
      {tab === "audience" && <AudienceTab report={report} prev={prev} />}
    </div>
  );
}
